import { getAcceptLanguageHeader } from '../i18n';

const API_BASE = '/api';

async function handleResponse(response) {
  if (!response.ok) {
    const text = await response.text();
    const message = text || 'Ошибка запроса статистики';
    throw new Error(message);
  }
  return response.json();
}

/**
 * Fetch revenue statistics with optional period filter.
 * @param {string} token - Bearer token
 * @param {string} period - 'today', '7', '30', 'all', or 'custom' (default: '7')
 * @param {string} dateFrom - Start date for custom period (YYYY-MM-DD)
 * @param {string} dateTo - End date for custom period (YYYY-MM-DD)
 * @returns {Promise<object>}
 */
export async function fetchRevenueStats(token, period = '7', dateFrom = '', dateTo = '') {
  if (!token) throw new Error('Не найден токен авторизации');
  
  const params = new URLSearchParams();
  if (period) params.set('period', period);
  if (period === 'custom' && dateFrom) params.set('date_from', dateFrom);
  if (period === 'custom' && dateTo) params.set('date_to', dateTo);
  
  const res = await fetch(`${API_BASE}/stats/revenue?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguageHeader(),
    },
  });
  const data = await handleResponse(res);
  return data?.data ?? null;
}

/**
 * Fetch cache status.
 * @param {string} token - Bearer token
 * @returns {Promise<object>}
 */
export async function fetchCacheStatus(token) {
  if (!token) throw new Error('Не найден токен авторизации');
  const res = await fetch(`${API_BASE}/stats/cache`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguageHeader(),
    },
  });
  const data = await handleResponse(res);
  return data?.data ?? null;
}

/**
 * Rebuild stats cache incrementally (only new days).
 * @param {string} token - Bearer token
 * @param {boolean} force - Force rebuild ignoring cooldown
 * @returns {Promise<object>}
 */
export async function rebuildStats(token, force = false) {
  if (!token) throw new Error('Не найден токен авторизации');
  const params = force ? '?force=1' : '';
  const res = await fetch(`${API_BASE}/stats/rebuild${params}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguageHeader(),
    },
  });
  const data = await handleResponse(res);
  return data?.data ?? null;
}

/**
 * Fully rebuild stats cache (drops existing data).
 * @param {string} token - Bearer token
 * @returns {Promise<object>}
 */
export async function rebuildStatsFull(token) {
  if (!token) throw new Error('Не найден токен авторизации');
  const res = await fetch(`${API_BASE}/stats/rebuild-full`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguageHeader(),
    },
  });
  const data = await handleResponse(res);
  return data?.data ?? null;
}

/**
 * Fetch basic stats (legacy endpoint).
 * @param {string} token - Bearer token
 * @returns {Promise<object>}
 */
export async function fetchStats(token) {
  if (!token) throw new Error('Не найден токен авторизации');
  const res = await fetch(`${API_BASE}/stats`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Accept-Language': getAcceptLanguageHeader(),
    },
  });
  const data = await handleResponse(res);
  return data?.data ?? null;
}
